// Vendor Profile

import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { VendorSpecificModal, VendorSpecificDisplay } from '../components/VendorSpecificModals';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { logChanges } from '../services/auditService';
import { formatPhoneDisplay } from '../utils/formatters';
import { computeServiceAreaStatusFields } from '../utils/serviceArea';
import { SHOW_INSURANCE_FEATURE } from '../constants/featureFlags';
import ImageCropModal from '../components/ImageCropModal';
import CroppedImage from '../components/CroppedImage';
import { TARGET_ASPECT_RATIO } from '../components/ImageCropModal';
import ConfirmModal from '../components/ConfirmModal';
import { getCountyForCity, getCountyForCityState, getCountiesWithinRadius } from '../utils/countyLookup';
import CityAutocomplete from '../components/CityAutocomplete';
import MediaRightsConfirmationModal from '../components/MediaRightsConfirmationModal';
import { logMediaRightsConfirmation } from '../utils/mediaRightsLogger';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// A gallery image entry can be either a plain URL string (older vendor
// records, before per-image crop data existed) or an object carrying the
// URL plus crop position ({ url, cropX, cropY, cropWidth, cropHeight }).
// This extracts just the URL regardless of which shape is present.
function getImageUrl(entry) {
  return typeof entry === 'string' ? entry : entry?.url;
}
L.Marker.prototype.options.icon = DefaultIcon;

function getZoomForRadius(miles) {
  if (miles <= 10) return 9.5;
  if (miles <= 25) return 8.5;
  if (miles <= 50) return 8;
  if (miles <= 75) return 7.5;
  if (miles <= 100) return 7;
  return 6.5;
}

export default function VendorProfile() {
  const [vendor, setVendor] = useState(null);
  const [uid, setUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBusinessInfo, setEditingBusinessInfo] = useState(false);
  const [editingServiceArea, setEditingServiceArea] = useState(false);
  const [editingSocialMedia, setEditingSocialMedia] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const mediaSectionRef = useRef(null);
  const [editingVendorSpecific, setEditingVendorSpecific] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [previewMode, setPreviewMode] = useState('full');
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/vendor/login');
        return;
      }
      setUid(user.uid);
      await loadVendor(user.uid);
    });
    return () => unsub();
  }, [navigate]);

  const loadVendor = async (userId, showFullPageLoading = true) => {
    if (showFullPageLoading) setLoading(true);
    const vendorDoc = await getDoc(doc(db, 'vendors', userId));
    if (vendorDoc.exists()) {
      setVendor({ id: userId, ...vendorDoc.data() });
    }
    if (showFullPageLoading) setLoading(false);
  };

  const saveField = async (fields, requiresReApproval = false) => {
    if (!uid) return;
    const oldData = vendor ? { ...vendor } : {};
    const updates = { ...fields };
    // Rejected vendors are excluded here on purpose — a rejection often
    // calls out several issues at once, and re-queuing them for review
    // after fixing just the first one is premature. They stay 'rejected'
    // through individual field edits and only move to 'pending_review'
    // once they explicitly use a "Submit for Review" action, once they've
    // actually finished addressing everything.
    if (requiresReApproval && vendor?.status !== 'pending' && vendor?.status !== 'rejected') {
      updates.status = 'pending_review';
    }
    await updateDoc(doc(db, 'vendors', uid), updates);
    try {
      await logChanges({ vendorId: uid, changedBy: 'vendor', changedByUid: uid, oldData, newData: updates });
    } catch (err) {
      console.error('Audit log error:', err);
    }
    // Refresh in the background — the page already has data to show, so
    // there's no need to flash the full-page loading state (which was also
    // resetting scroll position on every save, not just media uploads).
    await loadVendor(uid, false);
  };

  const handleRejectPendingReel = async () => {
    setMediaLoading(true);
    try {
      const storage = getStorage();
      const reelRef = ref(storage, `vendors/${uid}/reel/pending_reel.mp4`);
      await deleteObject(reelRef);
      await saveField({ pendingReelUrl: null });
    } catch (err) {
      console.error('Reject pending reel error:', err);
    }
    setMediaLoading(false);
  };

  const handleRejectPendingImage = async (index) => {
    setMediaLoading(true);
    try {
      const currentPending = [...(vendor.pendingImageUrls || [])];
      const [removed] = currentPending.splice(index, 1);
      if (removed?.url) {
        try {
          const storage = getStorage();
          const imgRef = ref(storage, removed.url);
          await deleteObject(imgRef);
        } catch (storageErr) {
          console.error('Pending image storage delete error (continuing anyway):', storageErr);
        }
      }
      await saveField({ pendingImageUrls: currentPending });
    } catch (err) {
      console.error('Reject pending image error:', err);
    }
    setMediaLoading(false);
  };

  const handleDeleteReel = async () => {
    setMediaLoading(true);
    try {
      const storage = getStorage();
      const reelRef = ref(storage, `vendors/${uid}/reel/reel.mp4`);
      await deleteObject(reelRef);
      await saveField({ reelUrl: null, hasReel: false });
    } catch (err) {
      console.error('Reel delete error:', err);
    }
    setMediaLoading(false);
  };

  const [draggedImageIndex, setDraggedImageIndex] = useState(null);

  const handleImageDrop = async (targetIndex) => {
    if (draggedImageIndex === null || draggedImageIndex === targetIndex) {
      setDraggedImageIndex(null);
      return;
    }
    const reordered = [...(vendor.imageUrls || [])];
    const [moved] = reordered.splice(draggedImageIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setDraggedImageIndex(null);
    await saveField({ imageUrls: reordered });
  };

  const handleReelSelect = (e) => {
    const file = e.target.files[0];
    if (!file || !uid) return;
    // Confirmation is required first — the actual upload only proceeds
    // once the modal is confirmed, matching the same rule as
    // registration but re-triggered every time new media is selected,
    // rather than remembered from a prior confirmation.
    setPendingReelFile(file);
    setShowMediaRightsModal(true);
  };

  const confirmPendingMedia = async () => {
    const mediaIds = [];
    if (vendor.pendingReelUrl) mediaIds.push(vendor.pendingReelUrl);
    (vendor.pendingImageUrls || []).forEach((img) => mediaIds.push(img.url));

    if (mediaIds.length === 0) return;

    await logMediaRightsConfirmation({
      vendorId: uid,
      userId: uid,
      mediaIds,
    });

    const updates = {};
    if (vendor.pendingReelUrl) {
      updates.reelUrl = vendor.pendingReelUrl;
      updates.hasReel = true;
      updates.pendingReelUrl = null;
    }
    if ((vendor.pendingImageUrls || []).length > 0) {
      updates.imageUrls = [...(vendor.imageUrls || []), ...vendor.pendingImageUrls];
      updates.imageCount = updates.imageUrls.length;
      updates.pendingImageUrls = [];
    }
    await saveField(updates);
  };

  const uploadPendingReel = async (file) => {
    setMediaLoading(true);
    try {
      const storage = getStorage();
      const reelRef = ref(storage, `vendors/${uid}/reel/reel.mp4`);
      await uploadBytes(reelRef, file);
      const url = await getDownloadURL(reelRef);
      await logMediaRightsConfirmation({
        vendorId: uid,
        userId: uid,
        mediaIds: [url],
      });
      await saveField({ reelUrl: url, hasReel: true });
    } catch (err) {
      console.error('Reel upload error:', err);
    }
    setMediaLoading(false);
    setTimeout(() => {
      mediaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const [cropQueue, setCropQueue] = useState([]);
  const [croppingFile, setCroppingFile] = useState(null);
  const [editingCropIndex, setEditingCropIndex] = useState(null);
  const [showMediaRightsModal, setShowMediaRightsModal] = useState(false);
  const [showPendingMediaConfirm, setShowPendingMediaConfirm] = useState(false);
  const [pendingReelFile, setPendingReelFile] = useState(null);
  const [pendingImageFiles, setPendingImageFiles] = useState(null);
  const [batchUploadedMediaIds, setBatchUploadedMediaIds] = useState([]);

  const uploadOneImage = async (file, cropData) => {
    const storage = getStorage();
    const currentUrls = [...(vendor.imageUrls || [])];
    const index = currentUrls.length;
    const imgRef = ref(storage, `vendors/${uid}/gallery/image_${index}.jpg`);
    await uploadBytes(imgRef, file);
    const url = await getDownloadURL(imgRef);
    const newUrls = [
      ...currentUrls,
      {
        url,
        cropX: cropData?.cropX ?? 0,
        cropY: cropData?.cropY ?? 0,
        cropWidth: cropData?.cropWidth ?? null,
        cropHeight: cropData?.cropHeight ?? null,
        panX: cropData?.panX ?? 0,
        panY: cropData?.panY ?? 0,
        zoom: cropData?.zoom ?? 1,
      },
    ];
    await saveField({ imageUrls: newUrls, imageCount: newUrls.length });
    return url;
  };

  const handleImagesSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!uid) return;
    const currentCount = (vendor.imageUrls || []).length;
    const remaining = 9 - currentCount;
    const toQueue = files.slice(0, remaining);
    if (toQueue.length === 0) return;
    // Confirmation required first — cropping/uploading only begins
    // once confirmed, re-triggered every time new images are selected.
    setPendingImageFiles(toQueue);
    setBatchUploadedMediaIds([]);
    setShowMediaRightsModal(true);
  };

  const startImageQueue = (toQueue) => {
    setCropQueue(toQueue);
    setCroppingFile({ file: toQueue[0], previewUrl: URL.createObjectURL(toQueue[0]) });
  };

  const advanceCropQueue = () => {
    const remainingQueue = cropQueue.slice(1);
    setCropQueue(remainingQueue);
    if (remainingQueue.length > 0) {
      setCroppingFile({ file: remainingQueue[0], previewUrl: URL.createObjectURL(remainingQueue[0]) });
    } else {
      setCroppingFile(null);
    }
  };

  const handleCropConfirm = async (cropData) => {
    const file = croppingFile.file;
    const remainingQueue = cropQueue.slice(1);
    advanceCropQueue();
    setMediaLoading(true);
    try {
      const uploadedUrl = await uploadOneImage(file, cropData);
      const allMediaIdsSoFar = [...batchUploadedMediaIds, uploadedUrl];
      setBatchUploadedMediaIds(allMediaIdsSoFar);

      // This was the last image in the batch — log the whole batch as
      // one confirmation, covering every image uploaded in this round.
      if (remainingQueue.length === 0) {
        await logMediaRightsConfirmation({
          vendorId: uid,
          userId: uid,
          mediaIds: allMediaIdsSoFar,
        });
        setBatchUploadedMediaIds([]);
      }
    } catch (err) {
      console.error('Image upload error:', err);
    }
    setMediaLoading(false);
    setTimeout(() => {
      mediaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleCropCancel = () => {
    advanceCropQueue();
  };

  const handleEditCropConfirm = async (cropData) => {
    const index = editingCropIndex;
    setEditingCropIndex(null);
    setMediaLoading(true);
    try {
      const urls = [...(vendor.imageUrls || [])];
      const existingUrl = getImageUrl(urls[index]);
      urls[index] = {
        url: existingUrl,
        cropX: cropData.cropX,
        cropY: cropData.cropY,
        cropWidth: cropData.cropWidth,
        cropHeight: cropData.cropHeight,
        panX: cropData.panX,
        panY: cropData.panY,
        zoom: cropData.zoom,
      };
      await saveField({ imageUrls: urls, imageCount: urls.length });
    } catch (err) {
      console.error('Crop update error:', err);
    }
    setMediaLoading(false);
  };

  const [confirmingDeleteIndex, setConfirmingDeleteIndex] = useState(null);
  const [confirmingSubmitForReview, setConfirmingSubmitForReview] = useState(false);

  const handleConfirmSubmitForReview = async () => {
    setConfirmingSubmitForReview(false);
    try {
      // The full rejection history (reason + timestamp for every time
      // this vendor was ever rejected) is already captured permanently
      // in rejectionHistory at the moment admin rejects — resubmitting
      // just needs to clear the *active* rejection and move status
      // forward, not duplicate that history into a separate field.
      await saveField({
        status: 'pending',
        rejectionReason: '',
        resubmittedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Submit for review error:', err);
    }
  };

  const deleteImage = (index) => {
    if (!uid) return;
    setConfirmingDeleteIndex(index);
  };

  const handleConfirmDeleteImage = async () => {
    const index = confirmingDeleteIndex;
    setConfirmingDeleteIndex(null);
    setMediaLoading(true);
    try {
      const urls = [...(vendor.imageUrls || [])];
      const urlToDelete = getImageUrl(urls[index]);
      urls.splice(index, 1);
      try {
        const storage = getStorage();
        const imgRef = ref(storage, urlToDelete);
        await deleteObject(imgRef);
      } catch (storageErr) {
        console.error('Storage cleanup error (continuing anyway):', storageErr);
      }
      await saveField({ imageUrls: urls, imageCount: urls.length });
    } catch (err) {
      console.error('Image delete error:', err);
    }
    setMediaLoading(false);
  };

  const safeString = (value) => {
    if (value == null) return '—';
    if (Array.isArray(value)) {
      if (value.length === 0) return '—';
      return value.join(', ');
    }
    if (value.toString().length === 0) return '—';
    return value.toString();
  };

  if (loading || !vendor) {
    return (
      <div style={styles.loading}>
        <p>Loading...</p>
      </div>
    );
  }

  const status = vendor.status || 'pending';
  const statusColor = status === 'approved' ? '#4CAF50' : status === 'rejected' ? '#E53935' : '#FF9800';
  const statusLabel = status === 'approved' ? 'Live' : status === 'rejected' ? 'Rejected' : status === 'pending_review' ? 'Under Review' : 'Pending Approval';

  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Link to="/" style={styles.logo}>PLND</Link>
          <Link to="/vendor/dashboard" style={styles.navLink}>← Back to Dashboard</Link>
        </div>
        <button onClick={() => { setShowPreview(true); setSelectedMedia(null); setPreviewMode('full'); }} style={styles.previewButton}>Preview Profile</button>
      </nav>

      <div style={styles.content}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>My Profile</h1>
            <p style={styles.companyName}>{vendor.companyName}</p>
          </div>
          <div style={{ ...styles.statusBadge, backgroundColor: statusColor + '20', border: `1px solid ${statusColor}40` }}>
            <div style={{ ...styles.statusDot, backgroundColor: statusColor }} />
            <span style={{ ...styles.statusText, color: statusColor }}>{statusLabel}</span>
          </div>
        </div>

        {vendor.serviceAreaMissing && (
          <div style={{ backgroundColor: '#FEE2E2', border: '1px solid rgba(185,28,28,0.3)', borderRadius: '14px', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', lineHeight: 1 }}>⚠️</span>
            <div>
              <p style={{ margin: 0, color: '#B91C1C', fontWeight: '700', fontSize: '14px' }}>Service Area Data Missing</p>
              <p style={{ margin: '4px 0 0', color: '#B91C1C', fontSize: '13px', lineHeight: 1.4 }}>
                You haven't set a service radius or added any cities, so couples searching by location won't be able to find you. Please update your Service Area to fix this.
              </p>
            </div>
          </div>
        )}

        {status === 'rejected' && (
          <div style={{ backgroundColor: '#FEE2E2', border: '1px solid rgba(185,28,28,0.3)', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
            <p style={{ margin: 0, color: '#B91C1C', fontWeight: '700', fontSize: '14px' }}>Application Rejected</p>
            {vendor.rejectionReason && (
              <p style={{ margin: '4px 0 0', color: '#B91C1C', fontSize: '13px', lineHeight: 1.4 }}>
                {vendor.rejectionReason}
              </p>
            )}
            <p style={{ margin: '8px 0 12px', color: '#B91C1C', fontSize: '12px', fontStyle: 'italic' }}>
              Make any needed changes below, then submit when you're ready — your profile won't be re-reviewed until you do.
            </p>
            <button
              onClick={() => setConfirmingSubmitForReview(true)}
              style={{ backgroundColor: '#B91C1C', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 18px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
            >
              Submit for Review
            </button>
          </div>
        )}

        {(vendor.pendingReelUrl || (vendor.pendingImageUrls || []).length > 0) && (
          <div style={{ backgroundColor: '#FEF3C7', border: '1px solid rgba(217,119,6,0.3)', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
            <p style={{ margin: 0, color: '#92400E', fontWeight: '700', fontSize: '14px' }}>New Content Awaiting Your Confirmation</p>
            <p style={{ margin: '4px 0 12px', color: '#92400E', fontSize: '13px', lineHeight: 1.4 }}>
              Admin added new {vendor.pendingReelUrl && vendor.pendingImageUrls?.length > 0 ? 'reel and images' : vendor.pendingReelUrl ? 'reel' : 'images'} to your profile. This won't be visible to anyone until you confirm you have the rights to it.
            </p>
            <button
              onClick={() => setShowPendingMediaConfirm(true)}
              style={{ backgroundColor: '#92400E', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 18px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
            >
              Review & Confirm
            </button>
          </div>
        )}

        {/* Account & Contact (locked) */}
        <div style={styles.lockedCard}>
          <div style={styles.lockedHeader}>
            <span style={styles.lockedIcon}>🔒</span>
            <span style={styles.lockedText}>These fields cannot be changed after registration.</span>
          </div>
          <div style={styles.divider} />
          <InfoRow label="Company" value={safeString(vendor.companyName)} />
          <InfoRow label="Contact Name" value={safeString(vendor.contactName)} />
          <InfoRow label="Email" value={safeString(vendor.email)} />
          <InfoRow label="Website" value={safeString(vendor.website)} />
          <InfoRow label="Phone" value={formatPhoneDisplay(vendor.phone)} />
          <InfoRow label="Vendor Type" value={safeString(vendor.vendorTypes?.[0])} />
          <InfoRow label="Business Address" value={vendor.businessAddress?.line1 ? `${vendor.businessAddress.line1}${vendor.businessAddress.line2 ? `, ${vendor.businessAddress.line2}` : ''}, ${vendor.businessAddress.city}, ${vendor.businessAddress.state} ${vendor.businessAddress.zip}` : '—'} />
        </div>

        {/* Business Information */}
        <SectionCard
          title="Business Information"
          onEdit={() => setEditingBusinessInfo(true)}
        >
          <InfoRow label="Phone Public" value={vendor.phonePublic ? 'Yes' : 'No'} />
          <InfoRow label="Contact Name Public" value={vendor.contactNamePublic ? 'Yes' : 'No'} />
          <InfoRow label="What Sets You Apart" value={(vendor.differentiators || []).filter((d) => d).join(' • ') || '—'} />
          <InfoRow label="Brief Intro" value={safeString(vendor.briefIntro)} />
          <InfoRow label="Bio" value={safeString(vendor.bio)} />
          <InfoRow label="Languages" value={safeString(vendor.languages)} />
          <InfoRow label="Diversity Tags" value={safeString(vendor.diversityTags)} />
          <InfoRow label="Experience" value={vendor.experienceYear ? `${vendor.experienceYears || 0} years (since ${vendor.experienceYear})` : '—'} />
          {SHOW_INSURANCE_FEATURE && (
            <InfoRow label="Has Insurance" value={vendor.hasInsurance ? 'Yes' : 'No'} />
          )}
        </SectionCard>

        {/* Service Area */}
        <SectionCard
          title="Service Area"
          onEdit={() => setEditingServiceArea(true)}
        >
          {(vendor.vendorTypes?.[0] === 'Venue' || vendor.vendorTypes?.[0] === 'Wedding Attire') ? (
            <InfoRow label="Location" value={`${vendor.businessAddress?.city || ''}, ${vendor.businessAddress?.state || ''}`} />
          ) : (
            <>
              <InfoRow label="Service Radius" value={vendor.serviceRadiusMiles ? `${vendor.serviceRadiusMiles} miles` : '—'} />
              <InfoRow label="Additional Cities" value={(vendor.additionalCities || []).join(', ') || '—'} />
            </>
          )}
          {vendor.latitude && vendor.longitude && (
            <div style={{ marginTop: '12px', position: 'relative', zIndex: 0 }}>
              <MapContainer
                center={[vendor.latitude, vendor.longitude]}
                zoom={vendor.vendorTypes?.[0] === 'Venue' || vendor.vendorTypes?.[0] === 'Wedding Attire' ? 13 : getZoomForRadius(vendor.serviceRadiusMiles || 25)}
                style={{ height: '220px', width: '100%', borderRadius: '12px' }}
                key={`${vendor.latitude}-${vendor.longitude}-${vendor.serviceRadiusMiles}`}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <Marker position={[vendor.latitude, vendor.longitude]} />
                {vendor.serviceRadiusMiles > 0 && vendor.vendorTypes?.[0] !== 'Venue' && vendor.vendorTypes?.[0] !== 'Wedding Attire' && (
                  <Circle
                    center={[vendor.latitude, vendor.longitude]}
                    radius={vendor.serviceRadiusMiles * 1609.34}
                    pathOptions={{ color: '#000000', fillColor: '#000000', fillOpacity: 0.1 }}
                  />
                )}
                {(vendor.additionalCitiesCoords || []).map((c, i) => (
                  <Marker key={i} position={[c.latitude, c.longitude]} />
                ))}
              </MapContainer>
            </div>
          )}
        </SectionCard>

        {/* Social Media */}
        <SectionCard
          title="Social Media"
          onEdit={() => setEditingSocialMedia(true)}
        >
          <InfoRow label="Instagram" value={safeString(vendor.instagram)} />
          <InfoRow label="Facebook" value={safeString(vendor.facebook)} />
          <InfoRow label="TikTok" value={safeString(vendor.tiktok)} />
          <InfoRow label="Pinterest" value={safeString(vendor.pinterest)} />
        </SectionCard>

        {/* Vendor Specific */}
        <SectionCard
          title="Vendor Specific Details"
          onEdit={() => setEditingVendorSpecific(true)}
        >
          <VendorSpecificDisplay vendor={vendor} safeString={safeString} />
        </SectionCard>

        {/* Media */}
        <div style={styles.card} ref={mediaSectionRef}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>Media</span>
          </div>
          <div style={styles.divider} />

          {mediaLoading && (
            <p style={{ textAlign: 'center', color: '#888', fontSize: '13px', marginBottom: '12px' }}>Updating media...</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#000000' }}>Intro Reel</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {!vendor.hiddenReelUrl && (
                <label style={{ ...styles.editButton, ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
                  Replace
                  <input type="file" accept="video/*" onChange={handleReelSelect} style={{ display: 'none' }} disabled={mediaLoading} />
                </label>
              )}
              {vendor.reelUrl && (
                <button
                  onClick={handleDeleteReel}
                  disabled={mediaLoading}
                  style={{ ...styles.editButton, color: '#B91C1C', ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
          {vendor.reelUrl ? (
            <video src={vendor.reelUrl} controls style={{ width: '100%', maxHeight: '280px', borderRadius: '12px', backgroundColor: '#000' }} />
          ) : vendor.hiddenReelUrl ? (
            <div style={{ position: 'relative' }}>
              <video src={vendor.hiddenReelUrl} controls style={{ width: '100%', maxHeight: '280px', borderRadius: '12px', backgroundColor: '#000' }} />
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', borderRadius: '12px', pointerEvents: 'none',
              }}>
                <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700', textAlign: 'center', padding: '0 12px' }}>Hidden from Users – Under Review</span>
              </div>
            </div>
          ) : vendor.pendingReelUrl ? null : (
            <label style={{ ...styles.mediaUploadBox, ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
              Tap to upload reel
              <input type="file" accept="video/*" onChange={handleReelSelect} style={{ display: 'none' }} disabled={mediaLoading} />
            </label>
          )}
          {vendor.pendingReelUrl && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ position: 'relative' }}>
                <video src={vendor.pendingReelUrl} controls style={{ width: '100%', maxHeight: '280px', borderRadius: '12px', backgroundColor: '#000' }} />
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', borderRadius: '12px', pointerEvents: 'none',
                }}>
                  <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700' }}>Needs User Approval</span>
                </div>
              </div>
              <button
                onClick={handleRejectPendingReel}
                disabled={mediaLoading}
                style={{ ...styles.editButton, color: '#B91C1C', marginTop: '8px', ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
              >
                Reject
              </button>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#000000' }}>
              Gallery ({(vendor.imageUrls || []).length}/9)
            </span>
            {(vendor.imageUrls || []).length < 9 && (
              <label style={{ ...styles.editButton, ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
                Add
                <input type="file" accept="image/*" multiple onChange={handleImagesSelect} style={{ display: 'none' }} disabled={mediaLoading} />
              </label>
            )}
          </div>
          {(vendor.imageUrls || []).length > 1 && (
            <p style={{ fontSize: '12px', color: '#888', marginTop: 0, marginBottom: '8px' }}>Drag and drop to reorder images.</p>
          )}
          <div style={styles.imageGrid}>
            {(vendor.imageUrls || []).map((entry, i) => (
              <div
                key={entry.url}
                style={{
                  ...styles.imageTile,
                  cursor: mediaLoading ? 'default' : 'grab',
                  opacity: draggedImageIndex === i ? 0.4 : 1,
                }}
                draggable={!mediaLoading}
                onDragStart={() => setDraggedImageIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleImageDrop(i)}
                onDragEnd={() => setDraggedImageIndex(null)}
              >
                <CroppedImage entry={entry} alt={`Gallery ${i + 1}`} style={styles.imageTileImg} />
                <button onClick={() => setEditingCropIndex(i)} style={styles.imageTileEdit} disabled={mediaLoading}>
                  <span className="material-icons" style={{ fontSize: '14px' }}>crop</span>
                </button>
                <button onClick={() => deleteImage(i)} style={styles.imageTileRemove} disabled={mediaLoading}>×</button>
              </div>
            ))}
            {(vendor.hiddenImageUrls || []).map((entry) => (
              <div key={entry.url} style={{ ...styles.imageTile, position: 'relative' }}>
                <CroppedImage entry={entry} alt="Hidden" style={styles.imageTileImg} />
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', textAlign: 'center', padding: '8px', borderRadius: 'inherit',
                  pointerEvents: 'none',
                }}>
                  <span style={{ color: '#fff', fontSize: '11px', fontWeight: '700' }}>Hidden from Users – Under Review</span>
                </div>
              </div>
            ))}
            {(vendor.pendingImageUrls || []).map((entry, i) => (
              <div key={`pending-${i}`} style={{ ...styles.imageTile, position: 'relative' }}>
                <CroppedImage entry={entry} alt={`Pending ${i + 1}`} style={styles.imageTileImg} />
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', textAlign: 'center', padding: '8px', borderRadius: 'inherit',
                }}>
                  <span style={{ color: '#fff', fontSize: '11px', fontWeight: '700' }}>Needs Vendor Approval</span>
                </div>
                <button onClick={() => handleRejectPendingImage(i)} style={styles.imageTileRemove} disabled={mediaLoading}>×</button>
              </div>
            ))}
          </div>
        </div>

      </div>

      {editingVendorSpecific && (
        <VendorSpecificModal
          vendor={vendor}
          onClose={() => setEditingVendorSpecific(false)}
          onSave={async (fields) => {
            await saveField(fields, true);
            setEditingVendorSpecific(false);
          }}
          requiresReApproval={true}
        />
      )}

      {editingSocialMedia && (
        <SocialMediaModal
          vendor={vendor}
          onClose={() => setEditingSocialMedia(false)}
          onSave={async (fields) => {
            await saveField(fields);
            setEditingSocialMedia(false);
          }}
        />
      )}

      {editingServiceArea && (
        <ServiceAreaModal
          vendor={vendor}
          onClose={() => setEditingServiceArea(false)}
          onSave={async (fields) => {
            await saveField(fields);
            setEditingServiceArea(false);
          }}
        />
      )}

      {editingBusinessInfo && (
        <BusinessInfoModal
          vendor={vendor}
          onClose={() => setEditingBusinessInfo(false)}
          onSave={async (fields) => {
            await saveField(fields);
            setEditingBusinessInfo(false);
          }}
        />
      )}
      {showPreview && (
        <div style={previewStyles.overlay} onClick={() => setShowPreview(false)}>
          <div style={previewStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={previewStyles.header}>
              <div>
                <span style={previewStyles.title}>Profile Preview</span>
                <p style={previewStyles.subtitle}>This is how couples see your profile</p>
              </div>
              <button onClick={() => setShowPreview(false)} style={previewStyles.closeButton}>×</button>
            </div>
            <div style={previewStyles.toggle}>
              <button
                onClick={() => setPreviewMode('full')}
                style={{ ...previewStyles.toggleBtn, ...(previewMode === 'full' ? previewStyles.toggleBtnActive : {}) }}
              >
                Full Profile
              </button>
              <button
                onClick={() => setPreviewMode('swipe')}
                style={{ ...previewStyles.toggleBtn, ...(previewMode === 'swipe' ? previewStyles.toggleBtnActive : {}) }}
              >
                Swipe Card
              </button>
            </div>

            {previewMode === 'swipe' && (
              <div style={previewStyles.swipeCard}>
                {/* Swipe card media */}
                {vendor.reelUrl ? (
                  <video src={vendor.reelUrl} autoPlay muted loop style={previewStyles.swipeMedia} />
                ) : vendor.imageUrls?.[0] ? (
                  <CroppedImage entry={vendor.imageUrls[0]} alt="Profile" style={previewStyles.swipeMedia} />
                ) : (
                  <div style={{ ...previewStyles.mediaPlaceholder, height: '280px' }}>No media uploaded</div>
                )}

                {/* Reel label */}
                <div style={previewStyles.reelLabel}>
                  <span className="material-icons" style={{ fontSize: '12px', color: 'white' }}>videocam</span>
                  <span style={{ fontSize: '12px', color: 'white', fontWeight: '500' }}>Intro Reel</span>
                </div>

                {/* Info section */}
                <div style={previewStyles.swipeInfo}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <span style={previewStyles.swipeName}>{vendor.companyName}</span>
                    {(vendor.differentiators || []).filter(d => d).length > 0 && (
                      <span style={previewStyles.swipeDiff}>{vendor.differentiators.filter(d => d).join(' | ')}</span>
                    )}
                  </div>
                  {vendor.businessAddress?.city && (
                    <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 8px' }}>{vendor.businessAddress.city}, {vendor.businessAddress.state}</p>
                  )}
                  <span style={previewStyles.swipeBadge}>{vendor.vendorTypes?.[0]}</span>
                  {vendor.briefIntro && (
                    <>
                      <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '10px 0' }} />
                      <p style={{ fontSize: '14px', color: '#555', fontStyle: 'italic', lineHeight: 1.4, margin: 0 }}>"{vendor.briefIntro}"</p>
                    </>
                  )}
                  {SHOW_INSURANCE_FEATURE && vendor.hasInsurance && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '10px', backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: '8px', padding: '4px 10px' }}>
                      <span className="material-icons" style={{ fontSize: '14px', color: '#000' }}>verified</span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#000' }}>Fully Insured</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {previewMode === 'full' && (
              <>
                {/* Main media */}
                {(() => {
                  if (selectedMedia === 'reel' || (selectedMedia === null && vendor.reelUrl)) {
                    return <video src={vendor.reelUrl} autoPlay muted loop style={previewStyles.media} />;
                  } else if (selectedMedia) {
                    return <CroppedImage entry={selectedMedia} alt="Selected" style={previewStyles.media} />;
                  } else if (vendor.imageUrls?.[0]) {
                    return <CroppedImage entry={vendor.imageUrls[0]} alt="Profile" style={previewStyles.media} />;
                  } else {
                    return <div style={previewStyles.mediaPlaceholder}>No media uploaded</div>;
                  }
                })()}

                {/* Gallery thumbnails */}
                {(vendor.reelUrl || (vendor.imageUrls || []).length > 0) && (
                  <div style={previewStyles.gallery}>
                    {vendor.reelUrl && (
                      <div
                        onClick={() => setSelectedMedia('reel')}
                        style={{
                          ...previewStyles.thumbContainer,
                          border: (selectedMedia === 'reel' || (selectedMedia === null && vendor.reelUrl)) ? '2px solid #000' : '2px solid transparent',
                        }}
                      >
                        <video src={vendor.reelUrl} style={previewStyles.thumb} muted />
                        <div style={previewStyles.reelOverlay}>▶</div>
                      </div>
                    )}
                    {(vendor.imageUrls || []).map((entry, i) => {
                      return (
                        <CroppedImage
                          key={i}
                          entry={entry}
                          alt={`Gallery ${i + 1}`}
                          onClick={() => setSelectedMedia(entry)}
                          style={{
                            ...previewStyles.thumb,
                            border: selectedMedia === entry ? '2px solid #000' : '2px solid transparent',
                            cursor: 'pointer',
                            borderRadius: '6px',
                          }}
                        />
                      );
                    })}
                  </div>
                )}

                <div style={previewStyles.body}>
                  {/* Name + differentiators */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h2 style={previewStyles.companyName}>{vendor.companyName}</h2>
                    {(vendor.differentiators || []).filter(d => d).length > 0 && (
                      <span style={previewStyles.differentiators}>{vendor.differentiators.filter(d => d).join(' | ')}</span>
                    )}
                  </div>

                  {/* Location */}
                  {vendor.businessAddress?.city && (
                    <p style={previewStyles.location}>{vendor.businessAddress.city}, {vendor.businessAddress.state}</p>
                  )}

                  {/* Type badge */}
                  <span style={previewStyles.typeBadge}>{vendor.vendorTypes?.[0]}</span>

                  {/* Detail rows */}
                  <div style={{ margin: '12px 0 4px' }}>
                    {vendor.experienceYears > 0 && (
                      <p style={previewStyles.detailRow}><span className="material-icons" style={previewStyles.detailIcon}>workspace_premium</span> {vendor.experienceYears} years in business</p>
                    )}
                    {(vendor.languages || []).length > 0 && (
                      <p style={previewStyles.detailRow}><span className="material-icons" style={previewStyles.detailIcon}>language</span> {vendor.languages.join(', ')}</p>
                    )}
                    {(vendor.diversityTags || []).length > 0 && (
                      <p style={previewStyles.detailRow}><span className="material-icons" style={previewStyles.detailIcon}>diversity_3</span> {vendor.diversityTags.join(', ')}</p>
                    )}
                    {SHOW_INSURANCE_FEATURE && vendor.hasInsurance && (
                      <p style={previewStyles.detailRow}><span className="material-icons" style={previewStyles.detailIcon}>verified</span> Insured</p>
                    )}
                  </div>



                  {/* Bio */}
                  {vendor.bio && (
                    <>
                      <p style={{ ...previewStyles.sectionTitle, marginTop: '24px' }}>About Us</p>
                      <p style={previewStyles.bio}>{vendor.bio}</p>
                    </>
                  )}

                  {/* Vendor Specific */}
                  {(() => {
                    const safeStr = (value) => {
                      if (value == null) return '—';
                      if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
                      if (value.toString().length === 0) return '—';
                      return value.toString();
                    };
                    return (
                      <div style={{ marginTop: '20px' }}>
                        <VendorSpecificDisplay vendor={vendor} safeString={safeStr} />
                      </div>
                    );
                  })()}

                  {/* Contact info */}
                  <div style={{ marginTop: '32px', textAlign: 'center' }}>
                    <p style={previewStyles.sectionTitle}>Contact Information</p>
                    {vendor.contactNamePublic && vendor.contactName && (
                      <p style={{ ...previewStyles.centeredRow, marginBottom: '12px' }}><span className="material-icons" style={previewStyles.detailIcon}>person</span> {vendor.contactName}</p>
                    )}
                    {vendor.phonePublic && vendor.phone && (
                      <p style={{ ...previewStyles.centeredRow, marginBottom: '12px' }}><span className="material-icons" style={previewStyles.detailIcon}>phone</span> {formatPhoneDisplay(vendor.phone)}</p>
                    )}
                    {vendor.email && (
                      <p style={{ ...previewStyles.centeredRow, marginBottom: '12px' }}><span className="material-icons" style={previewStyles.detailIcon}>email</span> {vendor.email}</p>
                    )}
                    {vendor.businessAddress && (
                      <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                        <div style={{ ...previewStyles.centeredRow, gap: '6px' }}>
                          <span className="material-icons" style={previewStyles.detailIcon}>location_on</span>
                          <span style={{ fontSize: '14px', color: '#555' }}>{vendor.businessAddress.line1}{vendor.businessAddress.line2 ? `, ${vendor.businessAddress.line2}` : ''}</span>
                        </div>
                        <p style={{ fontSize: '14px', color: '#555', margin: '2px 0 0' }}>{vendor.businessAddress.city}, {vendor.businessAddress.state} {vendor.businessAddress.zip}</p>
                      </div>
                    )}
                  </div>

                  {/* Social */}
                  {(vendor.instagram || vendor.facebook || vendor.tiktok || vendor.pinterest || vendor.website) && (
                    <div style={{ marginTop: '25px', textAlign: 'center' }}>
                      <p style={previewStyles.sectionTitle}>Find Us Online</p>
                      {vendor.website && (
                        <p style={previewStyles.centeredRow}>
                          <span className="material-icons" style={previewStyles.detailIcon}>language</span>
                          <a href={vendor.website} target="_blank" rel="noreferrer" style={{ color: '#000', fontSize: '14px' }}>{vendor.website}</a>
                        </p>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', marginTop: '25px' }}>
                        {vendor.instagram && (
                          <a href={vendor.instagram} target="_blank" rel="noreferrer" style={previewStyles.socialLink}>
                            <img src="https://cdn.simpleicons.org/instagram/000000" alt="Instagram" style={previewStyles.socialIcon} />
                          </a>
                        )}
                        {vendor.facebook && (
                          <a href={vendor.facebook} target="_blank" rel="noreferrer" style={previewStyles.socialLink}>
                            <img src="https://cdn.simpleicons.org/facebook/000000" alt="Facebook" style={previewStyles.socialIcon} />
                          </a>
                        )}
                        {vendor.tiktok && (
                          <a href={vendor.tiktok} target="_blank" rel="noreferrer" style={previewStyles.socialLink}>
                            <img src="https://cdn.simpleicons.org/tiktok/000000" alt="TikTok" style={previewStyles.socialIcon} />
                          </a>
                        )}
                        {vendor.pinterest && (
                          <a href={vendor.pinterest} target="_blank" rel="noreferrer" style={previewStyles.socialLink}>
                            <img src="https://cdn.simpleicons.org/pinterest/000000" alt="Pinterest" style={previewStyles.socialIcon} />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <footer style={{ borderTop: '1px solid #e8e8e0', padding: '24px 48px', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F5EF', flexWrap: 'wrap' }}>
        <a href="/terms" style={{ fontSize: '13px', color: '#555', textDecoration: 'none', fontWeight: '500' }}>Terms of Service</a>
        <span style={{ color: '#ccc', fontSize: '13px' }}>·</span>
        <a href="/privacy" style={{ fontSize: '13px', color: '#555', textDecoration: 'none', fontWeight: '500' }}>Privacy Policy</a>
        <span style={{ color: '#ccc', fontSize: '13px' }}>·</span>
        <a href="mailto:vndr@test.com" style={{ fontSize: '13px', color: '#555', textDecoration: 'none', fontWeight: '500' }}>Contact Us</a>
        <span style={{ color: '#ccc', fontSize: '13px' }}>·</span>
        <span style={{ fontSize: '13px', color: '#888' }}>© 2026 PLND. All rights reserved.</span>
      </footer>
      {showMediaRightsModal && (
        <MediaRightsConfirmationModal
          onCancel={() => {
            setShowMediaRightsModal(false);
            setPendingReelFile(null);
            setPendingImageFiles(null);
          }}
          onConfirm={() => {
            setShowMediaRightsModal(false);
            if (pendingReelFile) {
              uploadPendingReel(pendingReelFile);
              setPendingReelFile(null);
            }
            if (pendingImageFiles) {
              startImageQueue(pendingImageFiles);
              setPendingImageFiles(null);
            }
          }}
        />
      )}

      {showPendingMediaConfirm && (
        <MediaRightsConfirmationModal
          onCancel={() => setShowPendingMediaConfirm(false)}
          onConfirm={async () => {
            setShowPendingMediaConfirm(false);
            await confirmPendingMedia();
          }}
        />
      )}

      {croppingFile && (
        <ImageCropModal
          imageSrc={croppingFile.previewUrl}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
      {editingCropIndex !== null && (
        <ImageCropModal
          imageSrc={getImageUrl(vendor.imageUrls[editingCropIndex])}
          initialCrop={
            typeof vendor.imageUrls[editingCropIndex] === 'object'
              ? { x: vendor.imageUrls[editingCropIndex].panX ?? 0, y: vendor.imageUrls[editingCropIndex].panY ?? 0 }
              : undefined
          }
          initialZoom={
            typeof vendor.imageUrls[editingCropIndex] === 'object'
              ? vendor.imageUrls[editingCropIndex].zoom ?? 1
              : undefined
          }
          onCancel={() => setEditingCropIndex(null)}
          onConfirm={handleEditCropConfirm}
        />
      )}
      {confirmingDeleteIndex !== null && (
        <ConfirmModal
          message="Delete this image?"
          onCancel={() => setConfirmingDeleteIndex(null)}
          onConfirm={handleConfirmDeleteImage}
        />
      )}
      {confirmingSubmitForReview && (
        <ConfirmModal
          message="Submit your profile for review?"
          onCancel={() => setConfirmingSubmitForReview(false)}
          onConfirm={handleConfirmSubmitForReview}
        />
      )}
    </div>
  );
}

function SectionCard({ title, onEdit, children }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>{title}</span>
        <button onClick={onEdit} style={styles.editButton}>✏️ Edit</button>
      </div>
      <div style={styles.divider} />
      {children}
    </div>
  );
}

function BusinessInfoModal({ vendor, onClose, onSave }) {
  const [phonePublic, setPhonePublic] = useState(vendor.phonePublic || false);
  const [contactNamePublic, setContactNamePublic] = useState(vendor.contactNamePublic || false);
  const [briefIntro, setBriefIntro] = useState(vendor.briefIntro || '');
  const [bio, setBio] = useState(vendor.bio || '');
  const [diff1, setDiff1] = useState(vendor.differentiators?.[0] || '');
  const [diff2, setDiff2] = useState(vendor.differentiators?.[1] || '');
  const [diff3, setDiff3] = useState(vendor.differentiators?.[2] || '');
  const initialLanguages = vendor.languages || [];
  const knownLangs = ['English', 'Spanish', 'French', 'Mandarin', 'Cantonese', 'Japanese', 'Korean', 'Vietnamese', 'Tagalog', 'Arabic', 'Hindi', 'Portuguese', 'Italian', 'German', 'Russian'];
  const detectedOther = initialLanguages.find((l) => !knownLangs.includes(l));
  const [languages, setLanguages] = useState(
    detectedOther ? [...initialLanguages.filter((l) => l !== detectedOther), 'Other'] : initialLanguages
  );
  const [otherLanguage, setOtherLanguage] = useState(detectedOther || '');
  const [diversityTags, setDiversityTags] = useState(vendor.diversityTags || []);
  const [hasInsurance, setHasInsurance] = useState(vendor.hasInsurance || false);
  const [experienceYear, setExperienceYear] = useState(vendor.experienceYear ? vendor.experienceYear.toString() : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const ALL_LANGUAGES = [
    'English', 'Spanish', 'French', 'Mandarin', 'Cantonese', 'Japanese',
    'Korean', 'Vietnamese', 'Tagalog', 'Arabic', 'Hindi', 'Portuguese',
    'Italian', 'German', 'Russian', 'Other',
  ];
  const ALL_DIVERSITY_TAGS = [
    'Asian-Owned', 'Black-Owned', 'Hispanic/Latinx-Owned', 'LGBTQ+',
    'Native American-Owned', 'Pacific Islander-Owned', 'Veteran-Owned',
    'Women-Owned',
  ];

  const toggle = (value, array, setArray) => {
    if (array.includes(value)) {
      setArray(array.filter((v) => v !== value));
    } else {
      setArray([...array, value]);
    }
  };

  const handleSave = async () => {
    if (!diff1.trim() || !diff2.trim() || !diff3.trim()) {
      setError('Please fill in all 3 differentiators.');
      return;
    }
    if (!briefIntro.trim()) {
      setError('Brief intro is required.');
      return;
    }
    if (!bio.trim()) {
      setError('Bio is required.');
      return;
    }
    setSaving(true);
    setError('');
    const finalLanguages = languages.includes('Other') && otherLanguage.trim()
      ? [...languages.filter((l) => l !== 'Other'), otherLanguage.trim()]
      : languages;
    await onSave({
      phonePublic,
      contactNamePublic,
      briefIntro: briefIntro.trim(),
      bio: bio.trim(),
      differentiators: [diff1.trim(), diff2.trim(), diff3.trim()],
      languages: finalLanguages,
      diversityTags,
      hasInsurance,
      experienceYear: experienceYear ? parseInt(experienceYear) : 0,
      experienceYears: experienceYear ? (new Date().getFullYear() - parseInt(experienceYear)) : 0,
    });
    setSaving(false);
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.modalHeader}>
          <span style={modalStyles.modalTitle}>Edit Business Information</span>
          <button onClick={onClose} style={modalStyles.closeButton}>×</button>
        </div>
        {error && <p style={{ color: '#e53935', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}

        <div style={modalStyles.checkboxRow}>
          <input type="checkbox" checked={phonePublic} onChange={(e) => setPhonePublic(e.target.checked)} id="phonePublicEdit" />
          <label htmlFor="phonePublicEdit" style={modalStyles.checkboxLabel}>Show phone number publicly</label>
        </div>

        <div style={modalStyles.checkboxRow}>
          <input type="checkbox" checked={contactNamePublic} onChange={(e) => setContactNamePublic(e.target.checked)} id="contactPublicEdit" />
          <label htmlFor="contactPublicEdit" style={modalStyles.checkboxLabel}>Show contact name publicly</label>
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Differentiator 1 *</label>
          <input value={diff1} onChange={(e) => setDiff1(e.target.value.slice(0, 20))} style={modalStyles.input} maxLength={20} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Differentiator 2 *</label>
          <input value={diff2} onChange={(e) => setDiff2(e.target.value.slice(0, 20))} style={modalStyles.input} maxLength={20} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Differentiator 3 *</label>
          <input value={diff3} onChange={(e) => setDiff3(e.target.value.slice(0, 20))} style={modalStyles.input} maxLength={20} />
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Brief Intro *</label>
          <textarea value={briefIntro} onChange={(e) => setBriefIntro(e.target.value.slice(0, 160))} style={{ ...modalStyles.input, minHeight: '70px' }} maxLength={160} />
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Bio</label>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} style={{ ...modalStyles.input, minHeight: '100px' }} />
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Languages</label>
          <div style={modalStyles.chipRow}>
            {ALL_LANGUAGES.map((lang) => (
              <button
                key={lang}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggle(lang, languages, setLanguages)}
                style={{ ...modalStyles.chip, ...(languages.includes(lang) ? modalStyles.chipSelected : {}) }}
              >
                {lang}
              </button>
            ))}
          </div>
          {languages.includes('Other') && (
            <input
              value={otherLanguage}
              onChange={(e) => setOtherLanguage(e.target.value.slice(0, 15))}
              style={{ ...modalStyles.input, marginTop: '12px' }}
              placeholder="Specify other language"
              maxLength={15}
            />
          )}
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Diversity Tags</label>
          <div style={modalStyles.chipRow}>
            {ALL_DIVERSITY_TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggle(tag, diversityTags, setDiversityTags)}
                style={{ ...modalStyles.chip, ...(diversityTags.includes(tag) ? modalStyles.chipSelected : {}) }}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Year Started in Business</label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <select
              value={experienceYear}
              onChange={(e) => setExperienceYear(e.target.value)}
              style={{ ...modalStyles.input, flex: 2 }}
            >
              <option value="">Not specified</option>
              {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {experienceYear && (
              <div style={{ flex: 3, display: 'flex', alignItems: 'center', backgroundColor: '#F5F5EF', borderRadius: '10px', padding: '0 14px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#000000' }}>
                  {new Date().getFullYear() - parseInt(experienceYear)} years in business
                </span>
              </div>
            )}
          </div>
        </div>
        {SHOW_INSURANCE_FEATURE && (
          <div style={modalStyles.checkboxRow}>
            <input type="checkbox" checked={hasInsurance} onChange={(e) => setHasInsurance(e.target.checked)} id="hasInsuranceEdit" />
            <label htmlFor="hasInsuranceEdit" style={modalStyles.checkboxLabel}>Has Insurance</label>
          </div>
        )}

        <button onClick={handleSave} style={modalStyles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function ServiceAreaModal({ vendor, onClose, onSave }) {
  const isExact = vendor.vendorTypes?.[0] === 'Venue' || vendor.vendorTypes?.[0] === 'Wedding Attire';
  const RADIUS_OPTIONS = [25, 50, 75, 100];
  const [serviceRadius, setServiceRadius] = useState(
    RADIUS_OPTIONS.includes(vendor.serviceRadiusMiles) ? vendor.serviceRadiusMiles : 25
  );
  const [useCustomRadius, setUseCustomRadius] = useState(
    vendor.serviceRadiusMiles && !RADIUS_OPTIONS.includes(vendor.serviceRadiusMiles)
  );
  const [customRadius, setCustomRadius] = useState(
    vendor.serviceRadiusMiles && !RADIUS_OPTIONS.includes(vendor.serviceRadiusMiles) ? vendor.serviceRadiusMiles.toString() : ''
  );
  const [additionalCities, setAdditionalCities] = useState(vendor.additionalCities || []);
  const [newCity, setNewCity] = useState('');
  const [cityMarkers, setCityMarkers] = useState([]);
  const [saving, setSaving] = useState(false);

  const currentRadius = useCustomRadius ? (parseInt(customRadius) || 0) : serviceRadius;

  const addCity = async () => {
    const trimmed = newCity.trim();
    if (trimmed && !additionalCities.includes(trimmed)) {
      setAdditionalCities([...additionalCities, trimmed]);
      setNewCity('');
      const coords = await geocodeCity(trimmed);
      if (coords) setCityMarkers((prev) => [...prev, coords]);
    }
  };

  const removeCity = (c) => {
    setAdditionalCities(additionalCities.filter((city) => city !== c));
    setCityMarkers(cityMarkers.filter((m) => m.name !== c));
  };

  const geocodeCity = async (cityState) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityState)}&format=json&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon), name: cityState };
      }
    } catch (err) {
      console.error(err);
    }
    return null;
  };

  const handleSave = async () => {
    setSaving(true);
    const coords = [];
    for (const city of additionalCities) {
      const c = await geocodeCity(city);
      if (c) coords.push(c);
    }

    // Recalculated fresh every save (never merged/appended) — this
    // naturally replaces any stale counties from before, whether the
    // vendor changed their radius, added/removed a city, or switched to
    // exact-location-only.
    const homeCounties = vendor.businessAddress?.city
      ? (await getCountyForCity(vendor.businessAddress.city, vendor.businessAddress.state)) || []
      : [];
    let additionalCountiesLists = [];
    let radiusCounties = [];
    if (!isExact) {
      additionalCountiesLists = await Promise.all(
        additionalCities.map((c) => getCountyForCityState(c))
      );
      if (vendor.latitude && vendor.longitude) {
        radiusCounties = await getCountiesWithinRadius(
          vendor.latitude,
          vendor.longitude,
          currentRadius
        );
      }
    }
    const allCounties = [
      ...homeCounties,
      ...additionalCountiesLists.filter(Boolean).flat(),
      ...radiusCounties,
    ];
    const counties = [...new Set(allCounties)];

    const fields = {
      serviceRadiusMiles: isExact ? 0 : currentRadius,
      additionalCities: isExact ? [] : additionalCities,
      additionalCitiesCoords: isExact ? [] : coords,
      counties,
    };
    await onSave({
      ...fields,
      ...computeServiceAreaStatusFields(vendor, fields),
    });
    setSaving(false);
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.modalHeader}>
          <span style={modalStyles.modalTitle}>Edit Service Area</span>
          <button onClick={onClose} style={modalStyles.closeButton}>×</button>
        </div>

        {vendor.businessAddress && (
          <div style={{ backgroundColor: '#F5F5EF', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
            <p style={{ fontSize: '11px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Business Address</p>
            <p style={{ fontSize: '14px', color: '#2D2D2D', lineHeight: 1.5, margin: 0 }}>
              {vendor.businessAddress.line1}{vendor.businessAddress.line2 ? `, ${vendor.businessAddress.line2}` : ''}<br />
              {vendor.businessAddress.city}, {vendor.businessAddress.state} {vendor.businessAddress.zip}
            </p>
          </div>
        )}
        {vendor.latitude && vendor.longitude && (
          <div style={{ marginBottom: '20px', borderRadius: '12px', overflow: 'hidden', position: 'relative', zIndex: 0 }}>
            <MapContainer
              center={[vendor.latitude, vendor.longitude]}
              zoom={isExact ? 13 : getZoomForRadius(currentRadius)}
              style={{ height: '220px', width: '100%' }}
              key={`${vendor.latitude}-${vendor.longitude}-${currentRadius}`}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <Marker position={[vendor.latitude, vendor.longitude]} />
              {!isExact && currentRadius > 0 && (
                <Circle
                  center={[vendor.latitude, vendor.longitude]}
                  radius={currentRadius * 1609.34}
                  pathOptions={{ color: '#000000', fillColor: '#000000', fillOpacity: 0.1 }}
                />
              )}
              {cityMarkers.map((m, i) => (
                <Marker key={i} position={[m.latitude, m.longitude]} />
              ))}
            </MapContainer>
          </div>
        )}
        {isExact ? (
          <p style={{ fontSize: '13px', color: '#888' }}>
            Your business address is used as your exact location. To update your address, please contact support.
          </p>
        ) : (
          <>
            <div style={modalStyles.field}>
              <label style={modalStyles.label}>Service Radius</label>
              <div style={modalStyles.chipRow}>
                {RADIUS_OPTIONS.map((miles) => (
                  <button
                    key={miles}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setServiceRadius(miles);
                      setUseCustomRadius(false);
                      setCustomRadius('');
                    }}
                    style={{ ...modalStyles.chip, ...(!useCustomRadius && serviceRadius === miles ? modalStyles.chipSelected : {}) }}
                  >
                    {miles} miles
                  </button>
                ))}
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setUseCustomRadius(true)}
                  style={{ ...modalStyles.chip, ...(useCustomRadius ? modalStyles.chipSelected : {}) }}
                >
                  Custom
                </button>
              </div>
              {useCustomRadius && (
                <input
                  type="text"
                  inputMode="numeric"
                  value={customRadius}
                  onChange={(e) => setCustomRadius(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  style={{ ...modalStyles.input, marginTop: '12px' }}
                  placeholder="Custom radius (miles)"
                />
              )}
            </div>

            <div style={modalStyles.field}>
              <label style={modalStyles.label}>Additional Cities (City, State abbv.)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <CityAutocomplete
                  value={newCity}
                  onChange={setNewCity}
                  placeholder="City, State"
                  style={{ ...modalStyles.input, flex: 1 }}
                />
                <button type="button" onClick={addCity} style={{ padding: '12px 18px', backgroundColor: '#000000', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}>
                  Add
                </button>
              </div>
              {additionalCities.length > 0 && (
                <div style={{ ...modalStyles.chipRow, marginTop: '10px' }}>
                  {additionalCities.map((c) => (
                    <div key={c} style={{ ...modalStyles.chip, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{c}</span>
                      <span onClick={() => removeCity(c)} style={{ cursor: 'pointer' }}>×</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <button onClick={handleSave} style={modalStyles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function SocialMediaModal({ vendor, onClose, onSave }) {
  const [instagram, setInstagram] = useState(vendor.instagram || '');
  const [facebook, setFacebook] = useState(vendor.facebook || '');
  const [tiktok, setTiktok] = useState(vendor.tiktok || '');
  const [pinterest, setPinterest] = useState(vendor.pinterest || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      instagram: instagram.trim(),
      facebook: facebook.trim(),
      tiktok: tiktok.trim(),
      pinterest: pinterest.trim(),
    });
    setSaving(false);
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.modalHeader}>
          <span style={modalStyles.modalTitle}>Edit Social Media</span>
          <button onClick={onClose} style={modalStyles.closeButton}>×</button>
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Instagram</label>
          <input value={instagram} onChange={(e) => setInstagram(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Facebook</label>
          <input value={facebook} onChange={(e) => setFacebook(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>TikTok</label>
          <input value={tiktok} onChange={(e) => setTiktok(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Pinterest</label>
          <input value={pinterest} onChange={(e) => setPinterest(e.target.value)} style={modalStyles.input} />
        </div>

        <button onClick={handleSave} style={modalStyles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}



function InfoRow({ label, value }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={styles.infoValue}>{value}</span>
    </div>
  );
}

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 20px', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '560px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  modalTitle: { fontSize: '20px', fontWeight: '800', color: '#000000' },
  closeButton: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888', lineHeight: 1 },
  field: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '8px' },
  input: { width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  chipRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  chip: { padding: '8px 14px', borderRadius: '18px', border: '1.5px solid #e0e0e0', backgroundColor: 'white', color: '#666', fontSize: '13px', fontWeight: '600', cursor: 'pointer', outline: 'none' },
  chipSelected: { backgroundColor: '#000000', border: '1.5px solid #000000', color: 'white' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' },
  checkboxLabel: { fontSize: '14px', color: '#444' },
  saveButton: { width: '100%', padding: '14px', backgroundColor: '#000000', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
};

const previewStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 20px' },
  modal: { backgroundColor: 'white', borderRadius: '20px', width: '100%', maxWidth: '480px', overflow: 'hidden', marginBottom: '40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 24px 16px' },
  title: { fontSize: '18px', fontWeight: '800', color: '#000' },
  subtitle: { fontSize: '12px', color: '#888', margin: '4px 0 0' },
  closeButton: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888', lineHeight: 1 },
  media: { width: '100%', aspectRatio: TARGET_ASPECT_RATIO, objectFit: 'cover', display: 'block' },
  mediaPlaceholder: { height: '200px', backgroundColor: '#F5F5EF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: '14px' },
  gallery: { display: 'flex', gap: '4px', padding: '4px', backgroundColor: '#F5F5EF', overflowX: 'auto' },
  thumb: { width: '64px', height: `${64 / TARGET_ASPECT_RATIO}px`, objectFit: 'cover', flexShrink: 0, cursor: 'pointer' },
  body: { padding: '20px 24px 32px' },
  companyName: { fontSize: '26px', fontWeight: '800', color: '#000', margin: '0 0 4px 0' },
  differentiators: { fontSize: '11px', color: '#000', fontWeight: '600', textAlign: 'right', maxWidth: '140px', lineHeight: 1.6 },
  typeBadge: { display: 'inline-block', padding: '4px 12px', backgroundColor: '#F5F5EF', borderRadius: '6px', fontSize: '12px', fontWeight: '700', color: '#4A3550', margin: '8px 0' },
  location: { fontSize: '14px', color: '#555', margin: '4px 0 8px' },
  detailRow: { fontSize: '13px', color: '#555', margin: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' },
  detailIcon: { fontSize: '16px', color: '#000', flexShrink: 0 },
  briefIntro: { fontSize: '15px', fontStyle: 'italic', color: '#555', margin: '16px 0', lineHeight: 1.6, borderTop: '1px solid #eee', paddingTop: '16px' },
  sectionTitle: { fontSize: '15px', fontWeight: '700', color: '#000', margin: '0 0 8px 0' },
  bio: { fontSize: '14px', color: '#555', lineHeight: 1.7, margin: 0 },
  centeredRow: { fontSize: '14px', color: '#555', margin: '6px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  socialIcon: { width: '28px', height: '28px' },
  socialLink: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  thumbContainer: { position: 'relative', width: '64px', height: '64px', flexShrink: 0, borderRadius: '6px', overflow: 'hidden', cursor: 'pointer' },
  toggle: { display: 'flex', margin: '0 24px 16px', backgroundColor: '#F5F5EF', borderRadius: '10px', padding: '4px' },
  toggleBtn: { flex: 1, padding: '8px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', backgroundColor: 'transparent', color: '#888' },
  toggleBtnActive: { backgroundColor: '#000', color: '#fff' },
  swipeCard: { backgroundColor: '#F5F5EF', overflow: 'hidden' },
  swipeMedia: { width: '100%', aspectRatio: TARGET_ASPECT_RATIO, objectFit: 'cover', display: 'block' },
  reelLabel: { position: 'relative', display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '20px', padding: '4px 10px', width: 'fit-content', margin: '-28px 0 0 12px', zIndex: 1 },
  swipeInfo: { padding: '12px 16px 20px' },
  swipeName: { fontSize: '20px', fontWeight: '700', color: '#2D2D2D' },
  swipeDiff: { fontSize: '11px', color: '#000', fontWeight: '600', textAlign: 'right', maxWidth: '120px', lineHeight: 1.5 },
  swipeBadge: { display: 'inline-block', padding: '4px 10px', backgroundColor: 'rgba(74,53,80,0.15)', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#4A3550' },
  reelOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.3)', color: 'white', fontSize: '18px' },
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#F5F5EF', fontFamily: "'Inter', sans-serif" },
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: "'Inter', sans-serif" },
  nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 48px', backgroundColor: '#F5F5EF', borderBottom: '1px solid #e8e8e0', position: 'sticky', top: 0, zIndex: 100 },
  logo: { fontSize: '28px', fontWeight: '800', color: '#000000', letterSpacing: '4px', textDecoration: 'none' },
  navLink: { color: '#555', textDecoration: 'none', fontSize: '14px', fontWeight: '600' },
  content: { maxWidth: '800px', margin: '0 auto', padding: '48px 24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' },
  title: { fontSize: '32px', fontWeight: '800', color: '#000000', marginBottom: '4px' },
  companyName: { fontSize: '15px', color: '#888' },
  statusBadge: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '20px' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%' },
  statusText: { fontSize: '14px', fontWeight: '700' },
  lockedCard: { backgroundColor: 'white', borderRadius: '16px', padding: '24px', marginBottom: '20px', border: '1px solid rgba(0,0,0,0.08)' },
  lockedHeader: { display: 'flex', alignItems: 'center', gap: '8px' },
  lockedIcon: { fontSize: '14px' },
  lockedText: { fontSize: '13px', color: '#000000', fontWeight: '500' },
  divider: { height: '1px', backgroundColor: '#eee', margin: '16px 0' },
  infoRow: { display: 'flex', padding: '8px 0' },
  infoLabel: { width: '140px', fontSize: '13px', color: '#888', fontWeight: '500' },
  infoValue: { fontSize: '13px', color: '#2D2D2D', fontWeight: '600', flex: 1 },
  card: { backgroundColor: 'white', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontSize: '16px', fontWeight: '700', color: '#000000' },
  editButton: { background: 'none', border: 'none', color: '#000000', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  previewButton: { backgroundColor: '#000', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  mediaUploadBox: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '140px', backgroundColor: '#F5F5EF', borderRadius: '12px', border: '1.5px dashed #ccc', cursor: 'pointer', color: '#888', fontSize: '14px' },
  imageGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' },
  imageTile: { position: 'relative', aspectRatio: TARGET_ASPECT_RATIO, borderRadius: '8px', overflow: 'hidden' },
  imageTileEdit: { position: 'absolute', bottom: '4px', left: '4px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  imageTileImg: { width: '100%', height: '100%', objectFit: 'cover' },
  imageTileRemove: { position: 'absolute', top: '4px', right: '4px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '14px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
};